import { GoogleGenAI, Type } from "@google/genai";
import { ProfileData, MapIntelligence, UserCoords } from "../types";
import { get as _get } from "lodash";

const ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY || 'PLACEHOLDER_API_KEY' });

/**
 * Uses gemini-2.5-flash with googleMaps tool to get grounded data about an address.
 * Optionally uses user location to provide local context.
 */
export const getAddressIntelligence = async (
  address: string,
  userCoords?: UserCoords
): Promise<MapIntelligence> => {
  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: `Perform a detailed intelligence synthesis for the address: "${address}". 
      Identify its commercial significance, nearby business landmarks, and provide a verified status. 
      If it is a known business hub, mention key tenants or industry relevance.`,
      config: {
        tools: [{ googleMaps: {} }],
        toolConfig: userCoords
          ? {
              retrievalConfig: {
                latLng: userCoords,
              },
            }
          : undefined,
      },
    });

    const chunks = _get(response, "candidates[0].groundingMetadata.groundingChunks", []);
    const formattedLinks = (chunks || [])
      .filter((chunk: any) => _get(chunk, "maps", null))
      .map((chunk: any) => ({
        uri: _get(chunk, "maps.uri", ""),
        title: _get(chunk, "maps.title", "View on Google Maps"),
      }));

    return {
      text: _get(response, "text", "Location verification unavailable."),
      links: formattedLinks,
    };
  } catch (error) {
    console.error("Maps Grounding Error:", error);
    return { text: "Location intelligence currently offline.", links: [] };
  }
};

export const generateProfileData = async (
  name: string,
  title: string,
  industry: string,
  companySize: string,
  segment: string,
  objective: string
): Promise<ProfileData> => {
  const prompt = `
    Task: Create a highly detailed B2B Marketing Intelligence Profile for a person.
    Name: ${name}
    Title: ${title}
    Industry: ${industry}
    Company Size: ${companySize}
    Target Audience Segment: ${segment}
    Campaign Objective: ${objective}

    The profile must be professional, psychologically grounded (OCEAN model), and include actionable sales/marketing intelligence.
    Ensure the "company" object uses the provided industry and size.
    Include a field "address" which is a plausible business address in the location specified or a major tech hub.
    Use the following schema for the response.
  `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-pro-preview",
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            id: { type: Type.STRING },
            name: { type: Type.STRING },
            title: { type: Type.STRING },
            email: { type: Type.STRING },
            phone: { type: Type.STRING },
            location: { type: Type.STRING },
            address: { type: Type.STRING },
            linkedin: { type: Type.STRING },
            archetype: { type: Type.STRING },
            bestContact: { type: Type.STRING },
            bio: { type: Type.STRING },
            company: {
              type: Type.OBJECT,
              properties: {
                name: { type: Type.STRING },
                industry: { type: Type.STRING },
                size: { type: Type.STRING },
                website: { type: Type.STRING },
              },
              required: ["name", "industry", "size", "website"],
            },
            engagementHistory: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  type: { type: Type.STRING },
                  asset: { type: Type.STRING },
                  date: { type: Type.STRING },
                  intent: {
                    type: Type.STRING,
                    description: "High, Medium, or Low",
                  },
                },
                required: ["type", "asset", "date", "intent"],
              },
            },
            ocean: {
              type: Type.OBJECT,
              properties: {
                openness: { type: Type.NUMBER },
                conscientiousness: { type: Type.NUMBER },
                extraversion: { type: Type.NUMBER },
                agreeableness: { type: Type.NUMBER },
                neuroticism: { type: Type.NUMBER },
              },
              required: [
                "openness",
                "conscientiousness",
                "extraversion",
                "agreeableness",
                "neuroticism",
              ],
            },
            communication: {
              type: Type.OBJECT,
              properties: {
                description: { type: Type.STRING },
                keywords: { type: Type.ARRAY, items: { type: Type.STRING } },
              },
              required: ["description", "keywords"],
            },
            decisionMaking: {
              type: Type.OBJECT,
              properties: {
                description: { type: Type.STRING },
                pace: { type: Type.STRING },
                risk: { type: Type.STRING },
              },
              required: ["description", "pace", "risk"],
            },
            insights: {
              type: Type.OBJECT,
              properties: {
                closingSecret: { type: Type.STRING },
                clearAnswerProb: { type: Type.STRING },
                motivators: { type: Type.ARRAY, items: { type: Type.STRING } },
                phrasesToUse: { type: Type.ARRAY, items: { type: Type.STRING } },
                dislikes: { type: Type.ARRAY, items: { type: Type.STRING } },
              },
              required: [
                "closingSecret",
                "clearAnswerProb",
                "motivators",
                "phrasesToUse",
                "dislikes",
              ],
            },
            meetings: {
              type: Type.OBJECT,
              properties: {
                dos: { type: Type.ARRAY, items: { type: Type.STRING } },
                donts: { type: Type.ARRAY, items: { type: Type.STRING } },
              },
              required: ["dos", "donts"],
            },
            coldCalling: {
              type: Type.OBJECT,
              properties: {
                patternInterrupt: { type: Type.STRING },
                pace: { type: Type.STRING },
                tone: { type: Type.STRING },
                tactics: { type: Type.STRING },
                mistakes: { type: Type.STRING },
                ask: { type: Type.STRING },
                subconsciousDriver: { type: Type.STRING },
              },
              required: [
                "patternInterrupt",
                "pace",
                "tone",
                "tactics",
                "mistakes",
                "ask",
                "subconsciousDriver",
              ],
            },
            emailGuidelines: {
              type: Type.OBJECT,
              properties: {
                subjectExample: { type: Type.STRING },
                salutation: { type: Type.STRING },
                greeting: { type: Type.STRING },
                bulletPoints: { type: Type.STRING },
                closingLine: { type: Type.STRING },
                signOff: { type: Type.STRING },
                tone: { type: Type.STRING },
                length: { type: Type.STRING },
              },
              required: [
                "subjectExample",
                "salutation",
                "greeting",
                "bulletPoints",
                "closingLine",
                "signOff",
                "tone",
                "length",
              ],
            },
            socialActivity: {
              type: Type.OBJECT,
              properties: {
                posts: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: {
                      id: { type: Type.NUMBER },
                      topic: { type: Type.STRING },
                      content: { type: Type.STRING },
                      date: { type: Type.STRING },
                    },
                    required: ["id", "topic", "content", "date"],
                  },
                },
              },
              required: ["posts"],
            },
          },
          required: [
            "id",
            "name",
            "title",
            "email",
            "phone",
            "location",
            "address",
            "linkedin",
            "archetype",
            "bestContact",
            "bio",
            "company",
            "engagementHistory",
            "ocean",
            "communication",
            "decisionMaking",
            "insights",
            "meetings",
            "coldCalling",
            "emailGuidelines",
            "socialActivity",
          ],
        },
      },
    });
    
    const text = _get(response, "text", "{}");
    return JSON.parse(text);
  } catch (error) {
    console.error("Gemini Error:", error);
    throw error;
  }
};

export const generateOutreachDraft = async (
  profile: ProfileData,
  channel: string,
  tone: string,
  objective: string,
  extraContext: string = ""
): Promise<string> => {
  const prompt = `
    Task: Write a personalized outreach message.
    Target Persona: ${_get(profile, "name", "")} (${_get(profile, "title", "")})
    Archetype: ${_get(profile, "archetype", "")}
    Communication Style: ${_get(profile, "communication.description", "")}
    Objective: ${objective}
    Additional Context: ${extraContext}
    Channel: ${channel}
    Tone: ${tone}

    Guidelines:
    - Use insights: ${_get(profile, "insights.closingSecret", "")}.
    - Follow these specific email rules: ${_get(profile, "emailGuidelines.salutation", "")}, ${_get(profile, "emailGuidelines.greeting", "")}.
    - Keep it around ${_get(profile, "emailGuidelines.length", "")}.

    Output only the message text.
  `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: prompt,
    });
    return _get(response, "text", "Failed to generate draft.");
  } catch (error) {
    console.error("Gemini Error:", error);
    return "Error connecting to AI service.";
  }
};

export const generateSocialComment = async (
  profile: ProfileData,
  postContent: string,
  commentType: 'insightful' | 'supportive' | 'question'
): Promise<string> => {
  const prompt = `
    Task: Write a social media comment for ${_get(profile, "name", "")}'s post.
    Post Content: "${postContent}"
    Target Persona: ${_get(profile, "name", "")}
    Type: ${commentType}
    Tone: ${_get(profile, "communication.description", "")}

    Output only the comment text.
  `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: prompt,
    });
    return _get(response, "text", "Failed to generate comment.");
  } catch (error) {
    console.error("Gemini Error:", error);
    return "Error connecting to AI service.";
  }
};
